import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { TablePaginationActions } from '~/src/components/molecules'
import { useCurrency } from '~/src/hooks'
import { IItem, IPagination, PatchStockRequest } from '~/src/repositories/Stock/models/Stock'
import { formatMoney } from '~/src/utils/formatMoney'

interface IColumn {
  id: 'photo' | 'refNo' | 'name' | 'category' | 'supplier' | 'option' | 'loadingPort' | 'price'
  label: string | React.ReactNode
  minWidth?: number
  align?: 'right'
  render: (item: IItem, index: number) => React.ReactNode
}

interface ITableListItemProps {
  items: IItem[]
  pagination: IPagination
  onSubmit: (stockId: number, tags: PatchStockRequest) => void
  offset: number
  setOffset: React.Dispatch<React.SetStateAction<number>>
}

const makeLink = (itemId: number, stockType?: string) => {
  const path = stockType === 'YbCar' ? 'parts-only-detail' : 'detail'

  if (import.meta.env.VITE_DOMAIN_EC) {
    return `https://${import.meta.env.VITE_DOMAIN_EC}/en/item/${path}/${itemId}`
  }

  if (window.location.host.match(/kra-network\.com/)) {
    return `https://${window.location.host.replace('admin-console.', '')}/en/item/${path}/${itemId}`
  }

  return `https://${window.location.host.replace('admin.', 'ec.')}/en/item/${path}/${itemId}`
}

export const TableListItem: React.FC<ITableListItemProps> = ({
  items,
  pagination,
  onSubmit,
  offset,
  setOffset,
}): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limitValue)
  const { baseCurrencyUnit, unitPosition } = useCurrency()

  useEffect(() => {
    navigate(`/admin/items/list?page=${offset + 1}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset])

  const generatePatchStockRequestBody = (item: IItem) => {
    const body: PatchStockRequest = {
      tags: [],
    }
    if (!item.options.some((opt) => opt.id === 1)) {
      body.tags.push(1)
    }
    return body
  }

  const columns: IColumn[] = [
    {
      id: 'photo',
      label: `${t('list_item.photo')}`,
      minWidth: 150,
      render: ({ photo, name }) => <img height="100%" width="115" src={photo} alt={name} />,
    },
    {
      id: 'refNo',
      label: `${t('list_item.ref_no.')}`,
      minWidth: 150,
      render: (item) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          }}
        >
          <Link href={makeLink(item.id, item.stockType)} target="_blank" underline="always">
            {item.refNo}
          </Link>
        </Typography>
      ),
    },
    {
      id: 'name',
      label: `${t('list_item.name')}`,
      minWidth: 400,
      render: ({ name }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {name}
        </Typography>
      ),
    },
    {
      id: 'category',
      label: `${t('list_item.category')}`,
      minWidth: 150,
      render: ({ category }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {category}
        </Typography>
      ),
    },
    {
      id: 'supplier',
      label: `${t('list_item.supplier')}`,
      minWidth: 180,
      render: ({ supplier }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {supplier?.name ?? ''}
        </Typography>
      ),
    },
    {
      id: 'option',
      label: `${t('list_item.option')}`,
      minWidth: 150,
      render: (item, index) => (
        <Box ml={4}>
          <Checkbox
            size={'small'}
            onChange={() => onSubmit(item.id, generatePatchStockRequestBody(item))}
            color="primary"
            inputProps={{
              'aria-labelledby': `table-list-item-checkbox-${index}`,
            }}
            checked={item.options.some((opt) => opt.id === 1)}
          />
        </Box>
      ),
    },
    {
      id: 'loadingPort',
      label: `${t('list_item.loading_port')}`,
      minWidth: 150,
      render: (item) => (
        item.loadingPorts.map((port, index) => {
          return (
            <Typography
              key={index}
              variant="body2"
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
                color: 'rgba(0, 0, 0, 0.87)',
                marginBottom: '5px',
              }}
              >
              {port.name}
            </Typography>
          )
        })
      ),
    },
    {
      id: 'price',
      label: `${t('list_item.price')}`,
      minWidth: 150,
      render: (item) => (
        item.loadingPorts.map((port, index) => {
          return (
            <Typography
              key={index}
              variant="body2"
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
                color: 'rgba(0, 0, 0, 0.87)',
                marginBottom: '5px',
              }}
            >
              {formatMoney(item.price + port.transportationFee + port.loadingBaseFee, baseCurrencyUnit, unitPosition, item.stockType)}
            </Typography>
          )
        })
      ),
    },
  ]

  /**
   * handleChangePage
   * @param event
   * @param newPage
   */
  const handleChangePage = (event: unknown, newPage: number): void => {
    setOffset(newPage)
  }
  /**
   * handleChangeRowsPerPage
   * @param event
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setOffset(0)
  }

  return (
    <TableContainer component={Paper} sx={{ width: '100%', mt: '22px', padding: 1 }}>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.id}
                style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
              >
                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: '24px',
                      fontStyle: 'normal',
                      fontWeight: 'medium',
                    }}
                  >
                    {column.label}
                  </Typography>
                  <Typography
                    sx={{
                      width: '2px',
                      backgroundColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                      height: 14,
                    }}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items?.map((item, index) => (
            <TableRow hover role="checkbox" tabIndex={-12} key={index}>
              {columns.map((column, i) => (
                <TableCell key={i} align={column.align} sx={{ padding: '7.5px 8px' }}>
                  {column.render(item, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[]}
              rowsPerPage={pagination.limitValue}
              count={pagination.totalCount}
              page={offset}
              onPageChange={handleChangePage}
              sx={{
                fontSize: 12,
                fontStyle: 'normal',
                lineHeight: '19.92px',
                fontWeight: 400,
                borderColor: 'white',
              }}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}
